// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryDateGroupV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4
//
// Date eyebrow + a stack of MidasHistoryRowV2 components.
// Date buckets per spec §1: TODAY / YESTERDAY / THIS WEEK / EARLIER.
// Eyebrow style mirrors the sidebar history bucket label (JBM 11 uppercase).
import React from "react";

import { Thread } from "../../../midasPageModels";
import MidasHistoryRowV2 from "./MidasHistoryRowV2";

export type DateBucket = "today" | "yesterday" | "thisWeek" | "earlier";

interface MidasHistoryDateGroupV2Props {
  bucket: DateBucket;
  threads: Thread[];
  /** Map of machineId → display name, built once in the page component. */
  machineNameById: Record<string, string>;
  /** Currently-loaded thread id (or null) — drives row active indicator. */
  activeThreadId: string | null;
  /** Called when a row is clicked. */
  onOpenThread: (threadId: string) => void;
  /** Translation function — uses chatBot namespace keys. */
  t: (key: string, fallback: string) => string;
}

const bucketKey = (b: DateBucket): string => {
  switch (b) {
    case "today":
      return "midas.historyPage.bucketToday";
    case "yesterday":
      return "midas.historyPage.bucketYesterday";
    case "thisWeek":
      return "midas.historyPage.bucketThisWeek";
    case "earlier":
      return "midas.historyPage.bucketEarlier";
  }
};

const bucketFallback = (b: DateBucket): string => {
  switch (b) {
    case "today":
      return "TODAY";
    case "yesterday":
      return "YESTERDAY";
    case "thisWeek":
      return "THIS WEEK";
    case "earlier":
      return "EARLIER";
  }
};

const MidasHistoryDateGroupV2: React.FC<MidasHistoryDateGroupV2Props> = ({
  bucket,
  threads,
  machineNameById,
  activeThreadId,
  onOpenThread,
  t,
}) => {
  if (threads.length === 0) return null;

  const label = t(bucketKey(bucket), bucketFallback(bucket));

  return (
    // §5.4 audit: eyebrow → first-row gap bumped 8px → 16px (gap-2 → gap-4)
    // so the date label has the air the spec shows above the first card.
    <section aria-label={label} className="flex flex-col gap-4">
      <h2
        className="font-mono text-[11px] uppercase tracking-[0.14em] text-midas-muted-stone"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        {label}
      </h2>
      {/* Sub-phase 4.4a: rows are now hairline-bottom (no card border), so
          the previous `gap-3` between rows is gone — rows sit edge-to-edge
          and the 1px hairline separates them. List has no padding/margin
          of its own; rows own their own inset (px-3 py-3.5). */}
      <ul className="m-0 flex list-none flex-col p-0">
        {threads.map((thread) => {
          const machineLabel = thread.machine_id
            ? machineNameById[thread.machine_id] || null
            : null;
          return (
            <MidasHistoryRowV2
              key={thread.id}
              thread={thread}
              machineLabel={machineLabel}
              activeThreadId={activeThreadId}
              onOpen={onOpenThread}
              t={t}
            />
          );
        })}
      </ul>
    </section>
  );
};

export default MidasHistoryDateGroupV2;
